import { useState } from "react";
import { Banner, Button, Field, Input, StatusDot, useToast } from "@/shared/rd";
import { Actions, Meta, SectionCard } from "@/shared/stores/components/rd-local";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { intlLocale } from "@/lib/formatting/locale";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import {
  useSaveStorePaidLimitsMutation,
  useStorePaidLimitsQuery,
  type StorePaidLimits,
  type StorePaidLimitsView,
} from "@/modules/stores/services/storePaidLimits";

type LimitField = keyof StorePaidLimits;

/** The table's upper bounds (`store_paid_service_limits_values_check`). */
const MAX: Record<LimitField, number> = {
  routesDailyMax: 1_000_000,
  routesMonthlyMax: 100_000_000,
  whatsappMonthlyMax: 100_000_000,
  geminiMonthlyMax: 100_000_000,
};
const FIELDS: LimitField[] = ["routesDailyMax", "routesMonthlyMax", "whatsappMonthlyMax", "geminiMonthlyMax"];
const NO_LIMITS: StorePaidLimits = {
  routesDailyMax: null,
  routesMonthlyMax: null,
  whatsappMonthlyMax: null,
  geminiMonthlyMax: null,
};

type Draft = Record<LimitField, string>;

const draftOf = (limits: StorePaidLimits): Draft => ({
  routesDailyMax: limits.routesDailyMax === null ? "" : String(limits.routesDailyMax),
  routesMonthlyMax: limits.routesMonthlyMax === null ? "" : String(limits.routesMonthlyMax),
  whatsappMonthlyMax: limits.whatsappMonthlyMax === null ? "" : String(limits.whatsappMonthlyMax),
  geminiMonthlyMax: limits.geminiMonthlyMax === null ? "" : String(limits.geminiMonthlyMax),
});

/** A field's value, `null` when empty (follow the platform), or `undefined` when invalid. */
function parse(value: string, max: number): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  if (!/^\d+$/.test(trimmed)) return undefined;
  const n = Number(trimmed);
  return n <= max ? n : undefined;
}

/**
 * A store's own paid-service limits (super admins only, like the endpoint).
 *
 * Four numbers, each empty to follow the platform or 0 for no limit: Google
 * Routes per day (REPLACES the platform's per-store daily limit for this
 * store), and Google Routes, WhatsApp and Gemini per month (a store's own
 * monthly budget, on top of the platform's caps for all stores together).
 * Each says what the platform does and what the store used. Save writes all
 * four at once with an Undo; a failed save keeps the typed values and says so.
 */
export function StorePaidLimitsCard({ storeId, locale }: { storeId: string; locale: string }) {
  const t = useTranslations();
  const toast = useToast();
  const query = useStorePaidLimitsQuery(storeId);
  const save = useSaveStorePaidLimitsMutation();
  const number = new Intl.NumberFormat(intlLocale(locale));
  // The draft starts from the server's values and is re-seeded after a save.
  const [seededFrom, setSeededFrom] = useState<StorePaidLimitsView | null>(null);
  const [draft, setDraft] = useState<Draft>(draftOf(NO_LIMITS));
  const [tried, setTried] = useState(false);
  const [failure, setFailure] = useState<{ message: string; retry: () => void } | null>(null);

  if (query.data && query.data !== seededFrom) {
    setSeededFrom(query.data);
    setDraft(draftOf(query.data.limits));
    setTried(false);
  }

  const view = query.data;
  const parsed = Object.fromEntries(FIELDS.map((field) => [field, parse(draft[field], MAX[field])])) as Record<
    LimitField,
    number | null | undefined
  >;
  const invalid = FIELDS.some((field) => parsed[field] === undefined);
  const dirty = view ? FIELDS.some((field) => parsed[field] !== view.limits[field]) : false;
  const hasOwn = view ? FIELDS.some((field) => view.limits[field] !== null) : false;

  const write = async (limits: StorePaidLimits, message: string, previous: StorePaidLimits | null) => {
    setFailure(null);
    try {
      await save.mutateAsync({ storeId, limits });
      toast.show({
        tone: previous ? "neutral" : "success",
        message,
        ...(previous
          ? { actionLabel: t("admin.undo"), onAction: () => void write(previous, t("admin.storePaidLimitsUndone"), null) }
          : {}),
      });
    } catch (caught) {
      setFailure({
        message: caught instanceof ApiJsonClientError && caught.message ? caught.message : t(keys.errors.generic),
        retry: () => void write(limits, message, previous),
      });
    }
  };

  const submit = () => {
    setTried(true);
    if (!view || invalid || !dirty) return;
    void write(parsed as StorePaidLimits, t("admin.storePaidLimitsSaved"), view.limits);
  };

  const used = (count: number, today = false) =>
    t(today ? "admin.storePaidLimitsUsedToday" : "admin.storePaidLimitsUsedMonth", { count: number.format(count) });
  const capText = (cap: number) => (cap > 0 ? number.format(cap) : t("admin.storePaidLimitsUnlimited"));

  const rows: Array<{ field: LimitField; label: string; help: string; placeholder: string }> = view
    ? [
        {
          field: "routesDailyMax",
          label: t("admin.storePaidLimitsRoutesDay"),
          help: [
            view.platform.routesDailyMax > 0
              ? t("admin.storePaidLimitsPlatformDay", { value: number.format(view.platform.routesDailyMax) })
              : t("admin.storePaidLimitsPlatformNone"),
            used(view.usage.routesToday, true),
          ].join(" · "),
          placeholder:
            view.platform.routesDailyMax > 0
              ? t("admin.storePaidLimitsPlaceholderPlatform", { value: number.format(view.platform.routesDailyMax) })
              : t("admin.storePaidLimitsPlaceholderNone"),
        },
        {
          field: "routesMonthlyMax",
          label: t("admin.storePaidLimitsRoutesMonth"),
          help: [
            t("admin.storePaidLimitsAllStoresRoutes", {
              essentials: capText(view.platform.routesEssentialsMonthlyCap),
              pro: capText(view.platform.routesProMonthlyCap),
            }),
            used(view.usage.routesThisMonth),
          ].join(" · "),
          placeholder: t("admin.storePaidLimitsPlaceholderNone"),
        },
        {
          field: "whatsappMonthlyMax",
          label: t("admin.storePaidLimitsWhatsappMonth"),
          help: [
            t("admin.storePaidLimitsAllStores", { value: capText(view.platform.whatsappMonthlyCap) }),
            used(view.usage.whatsappThisMonth),
          ].join(" · "),
          placeholder: t("admin.storePaidLimitsPlaceholderNone"),
        },
        {
          field: "geminiMonthlyMax",
          label: t("admin.storePaidLimitsGeminiMonth"),
          help: [
            t("admin.storePaidLimitsAllStores", { value: capText(view.platform.geminiMonthlyCap) }),
            used(view.usage.geminiThisMonth),
            t("admin.storePaidLimitsGeminiNote"),
          ].join(" · "),
          placeholder: t("admin.storePaidLimitsPlaceholderNone"),
        },
      ]
    : [];

  return (
    <SectionCard
      badge="route"
      title={t("admin.storePaidLimitsTitle")}
      note={t("admin.storePaidLimitsDesc")}
      aside={
        view ? (
          hasOwn ? (
            <StatusDot tone="blue">{t("admin.storePaidLimitsOwn")}</StatusDot>
          ) : (
            <StatusDot tone="grey">{t("admin.storePaidLimitsPlatform")}</StatusDot>
          )
        ) : null
      }
    >
      {query.error && !view ? (
        <Banner
          tone="error"
          title={t("admin.storePaidLimitsLoadFailedTitle")}
          actions={
            <Button size="sm" loading={query.isRefetching} loadingLabel={t("admin.busyTrying")} onClick={() => void query.refetch()}>
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {t(keys.shell.errorSub)}
        </Banner>
      ) : null}
      {failure ? (
        <Banner
          tone="error"
          title={t("admin.storePaidLimitsSaveFailedTitle")}
          actions={
            <Button size="sm" loading={save.isPending} loadingLabel={t("admin.busyTrying")} onClick={failure.retry}>
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {failure.message} {t("admin.nothingChanged")}
        </Banner>
      ) : null}
      {query.isLoading ? <Meta>{t("admin.storePaidLimitsLoading")}</Meta> : null}

      {view ? (
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          {rows.map((row) => {
            const id = `store-paid-limit-${row.field}`;
            const error =
              tried && parsed[row.field] === undefined
                ? t("admin.storePaidLimitsInvalid", { max: number.format(MAX[row.field]) })
                : undefined;
            return (
              <Field key={row.field} label={row.label} htmlFor={id} help={row.help} error={error}>
                <Input
                  id={id}
                  name={row.field}
                  dir="ltr"
                  inputMode="numeric"
                  placeholder={row.placeholder}
                  value={draft[row.field]}
                  invalid={Boolean(error)}
                  disabled={save.isPending}
                  onChange={(event) => setDraft((prev) => ({ ...prev, [row.field]: event.target.value }))}
                  onBlur={() => setTried(true)}
                  style={{ width: 180 }}
                />
              </Field>
            );
          })}
          <Actions>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!dirty}
              loading={save.isPending}
              loadingLabel={t("admin.storePaidLimitsSaving")}
            >
              {t("admin.storePaidLimitsSave")}
            </Button>
            {hasOwn ? (
              <Button
                variant="ghost"
                size="sm"
                muted
                disabled={save.isPending}
                onClick={() => void write(NO_LIMITS, t("admin.storePaidLimitsReset"), view.limits)}
              >
                {t("admin.storePaidLimitsUsePlatform")}
              </Button>
            ) : null}
          </Actions>
        </form>
      ) : null}
    </SectionCard>
  );
}
